/**
 * Tutor Page
 *
 * Voice-based literacy tutor powered by ElevenLabs Conversational AI.
 * Dark theme exception (like consonant practice + reading views).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useSelectedChild } from '@/contexts/ChildContext';
import { useTutorSession } from '@/lib/voice-agent/hooks/useTutorSession';
import { TutorDisplay } from '@/components/tutor/TutorDisplay';
import { TutorControls } from '@/components/tutor/TutorControls';
import { SpeakerPicker } from '@/components/tutor/SpeakerPicker';
import { InactivityGuard } from '@/components/tutor/InactivityGuard';
import { FEATURES } from '@/lib/feature-flags';

export const TutorPage: React.FC = () => {
  // ALL hooks must be called unconditionally — before any early returns
  const { profile, loading: authLoading } = useAuth();
  const { selectedChild, isLoading: childLoading } = useSelectedChild();
  const [currentSpeaker, setCurrentSpeaker] = useState<string | undefined>();

  // ChildContext race fix: wait 3s for auth + child fetch to complete
  const [minWaitDone, setMinWaitDone] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinWaitDone(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // useTutorSession must be called on every render (Rules of Hooks).
  // Pass a stable childId — empty string when no child yet (hook won't
  // start a session without an explicit startSession() call).
  const childId = selectedChild?.id || '';
  const handleSpeakerChange = useCallback((name: string, _role: string) => {
    setCurrentSpeaker(name);
  }, []);

  const {
    status,
    visualState,
    sessionData,
    errorMessage,
    lastSpeechTimestampRef,
    startSession,
    endSession,
    updateSpeaker,
  } = useTutorSession({
    childId,
    onSpeakerChange: handleSpeakerChange,
  });

  const handleManualSpeakerSelect = useCallback((name: string, role: 'child' | 'adult') => {
    setCurrentSpeaker(name);
    updateSpeaker(name, role);
  }, [updateSpeaker]);

  // --- Early returns AFTER all hooks ---

  // Feature flag gate: tutor disabled
  if (!FEATURES.TUTOR) return <Navigate to="/" />;

  // Feature gate: /tutor only available on turneducation.com
  if (!window.location.hostname.includes('turneducation')) return <Navigate to="/" />;

  // Wait for data to load
  const isReady = (!!selectedChild) || minWaitDone;
  if (authLoading || childLoading || !isReady) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[#888888] text-lg">Loading...</div>
      </div>
    );
  }

  if (!profile || !selectedChild) return <Navigate to="/" />;

  // --- Render ---
  return (
    /* Dark theme exception: tutor page retains dark bg for optimal reading UX,
       same as consonant practice + reading views (per CLAUDE.md) */
    <div className="min-h-screen bg-white flex flex-col">
      {/* Visual display */}
      <div className="flex-1 flex items-center justify-center p-6">
        <TutorDisplay state={visualState} />
      </div>

      {/* Controls bar */}
      <div className="shrink-0 p-6 space-y-4 border-t border-[#E5E5E5]">
        {status === 'connected' && sessionData?.householdMembers && (
          <SpeakerPicker
            members={sessionData.householdMembers.map(m => ({
              name: m.name,
              role: m.role as 'child' | 'adult',
            }))}
            onSelect={handleManualSpeakerSelect}
            currentSpeaker={currentSpeaker}
          />
        )}

        {status === 'connecting' && (
          <div className="text-[#888888] text-center text-sm animate-pulse">
            Connecting to Addison...
          </div>
        )}

        <TutorControls
          status={status}
          onStart={startSession}
          onStop={endSession}
        />

        {errorMessage && (
          <p className="text-[#FF453A] text-sm text-center">{errorMessage}</p>
        )}

        {status === 'connected' && sessionData && (
          <p className="text-[#D4D4D4] text-xs text-center">
            {selectedChild.name} &middot; Module: {sessionData.dynamicVariables?.current_skill_description || ''}
          </p>
        )}
      </div>

      <InactivityGuard
        lastSpeechTimestamp={lastSpeechTimestampRef}
        onTimeout={endSession}
        enabled={status === 'connected'}
      />
    </div>
  );
};

export default TutorPage;
